/**
 * Shared entrance-reveal controller.
 *
 * One IntersectionObserver serves every [data-reveal] element on the page, and a
 * rAF-throttled scroll sweep backs it up. The sweep is not redundant: an observer
 * only samples at frame boundaries, so an element scrolled past between two
 * frames — a fast flick, an anchor jump, the End key — never reports as
 * intersecting and would otherwise stay hidden forever.
 *
 * Both paths converge on commit(). Targets are dropped as soon as they play, so
 * the pending set shrinks to nothing and the listeners detach.
 */

const pending = new Set<HTMLElement>()

/** Fraction of the viewport height an element's top must cross to play. */
const REVEAL_LINE = 0.92

let observer: IntersectionObserver | null = null
let frame = 0

function commit(element: HTMLElement) {
  if (!pending.delete(element)) return
  observer?.unobserve(element)
  element.classList.add('is-in')
  if (pending.size === 0) teardown()
}

function sweep() {
  frame = 0
  const line = window.innerHeight * REVEAL_LINE
  for (const element of [...pending]) {
    if (element.getBoundingClientRect().top < line) commit(element)
  }
}

function scheduleSweep() {
  // A hidden document produces no animation frames, so rAF would never fire and
  // pending elements would stay hidden even after the tab is brought forward.
  if (document.visibilityState === 'hidden') {
    sweep()
    return
  }
  if (!frame) frame = requestAnimationFrame(sweep)
}

function setup() {
  if (observer) return
  observer = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        // top < 0 means the element is already above the viewport.
        if (entry.isIntersecting || entry.boundingClientRect.top < 0) {
          commit(entry.target as HTMLElement)
        }
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.1 },
  )
  window.addEventListener('scroll', scheduleSweep, { passive: true })
  window.addEventListener('resize', scheduleSweep)
  document.addEventListener('visibilitychange', scheduleSweep)
}

function teardown() {
  observer?.disconnect()
  observer = null
  window.removeEventListener('scroll', scheduleSweep)
  window.removeEventListener('resize', scheduleSweep)
  document.removeEventListener('visibilitychange', scheduleSweep)
  if (frame) {
    cancelAnimationFrame(frame)
    frame = 0
  }
}

/** Registers an element and returns its cleanup function. */
export function registerReveal(element: HTMLElement): () => void {
  if (typeof IntersectionObserver === 'undefined') {
    element.classList.add('is-in')
    return () => {}
  }

  pending.add(element)
  setup()
  observer!.observe(element)
  scheduleSweep()

  return () => {
    if (pending.delete(element) && pending.size === 0) teardown()
    else observer?.unobserve(element)
  }
}
